import { AfterViewInit, Component, computed, forwardRef, inject, OnInit, signal, WritableSignal } from '@angular/core';
import { TooltipComponent } from '../../../../../../shared/components/ui/tooltip/tooltip.component';
import { IconComponent } from '../../../../../../shared/components/ui/icon/icon.component';
import { TranslatePipe } from '../../../../../../shared/pipes/translate/translate.pipe';
import { SearchComponent } from '../../../../../../shared/components/ui/search/search.component';
import { FromFieldGridActionCellComponent } from '../form-list-control/components/from-field-grid-action-cell/from-field-grid-action-cell.component';
import { FormFieldGridCellComponent } from '../form-list-control/components/form-field-grid-cell/form-field-grid-cell.component';
import { FormListControlComponent } from '../form-list-control/form-list-control.component';
import { FormControlComponent } from '../form-control/form-control.component';
import { GroupDescriptor, orderBy, SortDescriptor } from '@progress/kendo-data-query';
import { Record } from 'src/models/ts/collection-form-field.model';
import { DropTargetEvent } from '@progress/kendo-angular-utils';
import { calculateDestinationIndex } from 'src/app/shared/components/grid/helpers/calculate-destination-index';
import { formsActions } from 'src/app/store/features/forms/forms-actions';
import { CollectionListDataInstance } from '../../../../../../shared/interfaces/collection-list-data-instance';
import { ProtectedFieldType } from '../../../../../../../models/ts/protected-field-type.model';
import { ToggleArrowComponent } from '../../../../../../shared/components/ui/toggle-arrow/toggle-arrow.component';
import { ChecklistsGroupSortModalComponent } from './modals/checklists-group-sort-modal/checklists-group-sort-modal.component';
import {
  selectForm,
  selectFormFieldValues,
  selectGridValues
} from '../../../../../../store/features/forms/forms-selectors';
import { distinctUntilChanged, filter, take } from 'rxjs';
import { StoreCollectionForm } from '../../../../../../store/features/forms/forms-state';
import { ProtectedCollectionType } from '../../../../../../../models/ts/protected-collection-type.model';
import { TableFieldDataType } from '../../../../../../../models/ts/table-field-data-type.model';
import { QuestionBulkGroupComponent } from './modals/question-bulk-group/question-bulk-group.component';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { EditorModule } from '@progress/kendo-angular-editor';
import { GridColumnBase } from '../../../../../../shared/classes/list/grid-column-base';
import { LinkedCollectionType } from '../../../../../../../models/ts/linked-collection-type.model';
import {
  CollectionFieldListOptionsConfiguration,
  CollectionFormFieldValue
} from 'src/models/ts/collection-form-field-value.model';
import { FieldSortingType } from 'src/models/ts/field-sorting-type.model';
import { userSettingsFeature } from 'src/app/store/features/user-settings/user-settings-feature';
import { StateType } from 'src/models/ts/state-type.model';
import { ExecuteChecklistControlComponent } from '../execute-checklist-control/execute-checklist-control.component';
import { CollectionFormService } from '../../../services/collection-form.service';
import { UpdateDeleteState } from '../../../../../../../models/ts/update-delete-state.model';
import { ExecuteChecklistPreviewComponent } from 'src/app/features/bizzmine/form/components/controls/edit-checklist-control/components/execute-checklist-preview/execute-checklist-preview.component';
import { PopupComponent as BizzPopupComponent } from 'src/app/shared/components/ui/popup/popup.component';
import { PopupComponent } from '@progress/kendo-angular-popup';
import { ofType } from '@ngrx/effects';
import { LicenseApiService } from 'src/app/api/bizzmine/license-service/license-api.service';
import { LicenseKeyType } from 'src/models/ts/license-key-type.model';
import { GridComponent } from 'src/app/shared/components/grid/grid.component';

@Component({
  standalone: true,
  selector: 'bizz-edit-checklists-control',
  templateUrl: './edit-checklist-control.component.html',
  styleUrls: ['./edit-checklist-control.component.scss'],
  imports: [TooltipComponent, IconComponent, TranslatePipe,
    SearchComponent,
    FromFieldGridActionCellComponent,
    FormFieldGridCellComponent,
    forwardRef(() => FormControlComponent),
    ToggleArrowComponent,
    PopupComponent,
    EditorModule,
    ExecuteChecklistControlComponent,
    ExecuteChecklistPreviewComponent, PopupComponent, PopupComponent, BizzPopupComponent, GridComponent]
})
export class EditChecklistControlComponent extends FormListControlComponent implements OnInit, AfterViewInit {
  public rowReorderable: boolean = false;
  public searchTerm: string | undefined;
  public hasChkLicense = false;

  public groups: GroupDescriptor[] = [];
  public sorting: SortDescriptor[] = [];

  private licenseService = inject(LicenseApiService);

  public isChkTemplates = computed(() => {
    return this.form()?.data.ProtectedCollectionType == ProtectedCollectionType.ChkTemplates;
  });

  public save(): void {
    const form = this.form();
    if(form){
      this.store$.dispatch(formsActions.invokeFormSave({ formId: form.id ,closeStep: false, closeForm: false }));
    }
  }

  public hasScheduler = computed(() => {
    return this.form()?.schedulerData != null;
  });

  private hasChecklistLicense$ = this.licenseService.hasLicense(LicenseKeyType.Checklists);

  public override ngOnInit(): void {
    super.ngOnInit();

    this.checkAndSetLicenseRestriction();

    //if in execute mode, we don't need to do anything.
    if (!this.isChkTemplates())
      return;

    this.setReadonlyOverride();
    this.adjustGridOptions();
    this.initOrderField();
    this.initGroupField();

    this.removedRecordsFromGrid();
    this.gridOptions.sortable = false;


    //Enable checkboxes
    this.gridOptions.selectable = { enabled: true, mode: 'multiple', checkboxOnly: true };

    //Sort By Order.
    const records = structuredClone(this.records());
    records.sort(this.rowSortFn.bind(this));
    this.updateRecords(records);

    //Get checklist groups for sorting
    const collectionId = this.form()?.data?.CollectionsID;
    if (collectionId != undefined && collectionId > 0 && this.checklistGroupValues.length > 0) {
      this.collectionListApiService.getCollectionFieldListOptionsConfiguration({
        CollectionId: collectionId,
        CollectionFieldValues: this.checklistGroupValues
      }).pipe(take(1)).subscribe((groups: CollectionFieldListOptionsConfiguration[]) => {
        this.checklistGroups = groups;
      });
    } else console.error('EditChecklistControl: CollectionId or checklistGroupValues is not set in the form data.');
  }

  // Function to always check the license and set licenseRestricted
  private checkAndSetLicenseRestriction(): void {
    this.hasChecklistLicense$.pipe(take(1)).subscribe(hasLicense => {
      this.hasChkLicense = hasLicense;
    });
  }


  public hasRecords = computed(() => {
    const field = this.formFieldSignal();
    if (field == null) {
      return false;
    }
    const form = this.form();
    if (form == null) {
      return false;
    }
    const vds = form.data.ViewDataSources.find(v => v.ViewDataSourcesID == field.ViewDataSourcesID);
    if (vds) {
      const vdsFields = CollectionFormService.extractFieldsFromForm(form.data)
        .filter(field => field.ViewDataSourcesID == field.ViewDataSourcesID);
      const chkTemplateName = vdsFields.find(_ => _.ProtectedFieldType == ProtectedFieldType.ChkTemplateName);
      if (chkTemplateName) {
        const questionsVds = form.data.ViewDataSources.find(v => v.DataDesignViewDataSourcesID == field.ViewDataSourcesID
          && v.ProtectedCollectionType == ProtectedCollectionType.ChkLinkMainCollectionQuestions);
        return questionsVds?.Instances.some(i => i.State != UpdateDeleteState.Delete) ?? false;
      }
    }
    return false;
  });

  public canShowExecuteMode = computed(() => {
    const field = this.formFieldSignal();
    if (field == null) {
      return false;
    }
    const form = this.form();
    if (form == null) {
      return false;
    }
    const vds = form.data.ViewDataSources.find(v => v.ViewDataSourcesID == field.ViewDataSourcesID);
    if (vds) {
      const vdsFields = CollectionFormService.extractFieldsFromForm(form.data).filter(field =>
        field.ViewDataSourcesID == field.ViewDataSourcesID);
      const chkTemplateName = vdsFields.find(_ => _.ProtectedFieldType == ProtectedFieldType.ChkTemplateName);
      if (chkTemplateName) {
        const questionsVds = form.data.ViewDataSources.find(v => v.DataDesignViewDataSourcesID == field.ViewDataSourcesID &&
          v.ProtectedCollectionType == ProtectedCollectionType.ChkLinkMainCollectionQuestions);
        return questionsVds?.Instances.some(i => i.State != UpdateDeleteState.Delete &&
          i.CrossLinkedInstancesID > 0 && i.RowDataDesignCrossID > 0) ?? false;
      }
    }
    return false;
  });

  public checklistGroups: CollectionFieldListOptionsConfiguration[] = [];
  public lastUsedGroups: Array<GroupDescriptor> = [];

  //The id of the order field for ordering.
  private orderRecordFieldId = -1;

  private chkTemplateQuestionOrderIndex = -1;
  private chkTemplateQuestionGroupIndex = -1;


  private categoryLookupData: CollectionListDataInstance[] = [];

  private sortedRecords = computed(() => {
    return orderBy(this.records(), [...this.groups, ...this.sorting]);
  });

  public constructor() {
    super();
  }

  public get checklistGroupValues(): any[] {
    return this.store$.selectSignal(selectGridValues(this.formId, TableFieldDataType.ChkQuestionsCreateEditModus,
      ProtectedFieldType.ChkTemplateQuestionCrosslinkGroupCombobox))();
  }

  public selection: Array<Record> = [];

  private initOrderField(): void {
    const records = this.records();
    if (records.length > 0) {
      this.chkTemplateQuestionOrderIndex = records[0].Fields.findIndex(f => f.ProtectedFieldType == ProtectedFieldType.ChkTemplateQuestionCrosslinkOrderID);
      if (this.chkTemplateQuestionOrderIndex < 0) {
        console.error('Order field not found');
        return;
      }
      this.orderRecordFieldId = records[0].Fields[this.chkTemplateQuestionOrderIndex].CollectionFieldsID;
    }
  }

  private initGroupField(): void {
    const records = this.records();
    if (records.length > 0) {
      this.chkTemplateQuestionGroupIndex = records[0].Fields.findIndex(f => f.ProtectedFieldType == ProtectedFieldType.ChkTemplateQuestionCrosslinkGroupCombobox);
      if (this.chkTemplateQuestionGroupIndex < 0) {
        console.error('Category field not found');
      }
    }
  }

  private initNCNAValues(): void {
    this.store$.select(selectFormFieldValues(this.formId, {}, [ProtectedFieldType.ChkTemplateIsPossibleAnswerNA, ProtectedFieldType.ChkTemplateIsPossibleAnswerNC]))
      .pipe(takeUntilDestroyed(this.destroyRef),
        filter((_) => _ != null),
        distinctUntilChanged((prev, curr) => {
          return JSON.stringify(curr) == JSON.stringify(prev);
        })).subscribe((values: any) => {
      if (values) {
        const hasNa = values[ProtectedFieldType.ChkTemplateIsPossibleAnswerNA] as boolean;
        const hasNc = values[ProtectedFieldType.ChkTemplateIsPossibleAnswerNC] as boolean;
        this.hideOrShowNcNaBasedOnValues(hasNa, hasNc);
      }
    });
  }

  private setReadonlyOverride(): void {
    this.store$.select(selectForm(this.formId)).pipe(take(1)).subscribe((form: StoreCollectionForm | undefined) => {
      if (form?.data && form.data.ProtectedCollectionType == ProtectedCollectionType.None) {
        this.readOnlyOverride = true;
      }
    });
  }

  private removedRecordsFromGrid(): void {
    this.actions$.pipe(ofType(formsActions.removeRecordFromGrid, formsActions.removeRecordsFromGrid), takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.selection = [];
    });
  }

  public titleCheckBoxClicked(event: any, column: GridColumnBase): void {
    this.records().forEach(record => {
      const field = record.Fields.find(f => f.ProtectedFieldType == column.ProtectedFieldType);
      if (field) {
        this.store$.dispatch(formsActions.updateGridFormFieldValue({
          formId: this.formId,
          value: event.target.checked,
          recordId: record.CrossLinkedInstancesID,
          gridFieldId: this.formFieldId()!,
          recordFieldId: field.CollectionFieldsID
        }));
      }
    });
  }

  public isAllSelected(column: GridColumnBase) {
    return this.records().map(_ => _.Fields.find(f => f.ProtectedFieldType == column.ProtectedFieldType)).every(_ => _?.Value == true);
  }

  public toggleRowReordering(): void {
    this.rowReorderable = !this.rowReorderable;
    //Sorting can't work with row reordering
    //this.gridOptions.sortable = !this.rowReorderable;
    if (this.rowReorderable) {
      this.searchTermSignal.set('');
      this.searchTerm = '';
      this.selection = [];

      this.setRowEditMode(false);
      //Sort By Order.
      this.sorting = this.gridOptions.dataSource.sort = [{
        field: `Fields[${this.chkTemplateQuestionOrderIndex}].Value`,
        compare: this.rowSortFn.bind(this)
      }];
      this.lastUsedGroups = this.gridOptions.dataSource.group;

      //TODO: RV Don't group questions if there is only one or no group(s).
      if (this.checklistGroupValues.length > 1) {
        this.groups = this.gridOptions.dataSource.group = [{
          field: `Fields[${this.chkTemplateQuestionGroupIndex}].Value`,
          compare: this.rowSortFn.bind(this)
        }];
      } else {
        this.groups = this.gridOptions.dataSource.group = [];
      }

      //Workaround to set the cursor after row reordering is enabled and the grid is updated from changing the groups.
      setTimeout(() => {
        this.gridComponent.updateMouseCursor('move');
      }, 300);
      this.gridOptions.selectable.enabled = this.gridOptions.groupable.enabled = false;
    } else {
      this.gridOptions.selectable.enabled = this.gridOptions.groupable.enabled = true;
      this.gridOptions.dataSource.group = this.lastUsedGroups;
      this.gridOptions.dataSource.sort = [];
    }
  }

  private setRowEditMode(editMode: boolean): void {
    //Turn off EditMode for rows
    const records = structuredClone(this.sortedRecords());
    records.forEach(r => r.EditMode = editMode);
    this.updateRecords(records);
  }

  private rowSortFn(rowA: any, rowB: any): number {
    const groupASortIndex = this.checklistGroups.find(g => g.TableFieldValueId ==
      rowA.Fields[this.chkTemplateQuestionGroupIndex].Value)?.IndexPosition ?? -1;
    const groupBIndex = this.checklistGroups.find(g => g.TableFieldValueId ==
      rowB.Fields[this.chkTemplateQuestionGroupIndex].Value)?.IndexPosition ?? -1;
    if (groupASortIndex - groupBIndex == 0) {
      return rowA.Fields[this.chkTemplateQuestionOrderIndex].Value - rowB.Fields[this.chkTemplateQuestionOrderIndex].Value;
    } else return groupASortIndex - groupBIndex;
  }

  private adjustGridOptions(): void {
    this.initNCNAValues();
    this.hideCustomColumns();
    //Adjust grid options for the checklist control
    this.gridOptions.columns.forEach(c => {
      if (c.ProtectedFieldType == ProtectedFieldType.ChkTemplateQuestionCrosslinkIsPossibleAnswerNA ||
        c.ProtectedFieldType == ProtectedFieldType.ChkTemplateQuestionCrosslinkIsPossibleAnswerNC ||
        c.ProtectedFieldType == ProtectedFieldType.ChkTemplateQuestionCrosslinkIsRemarkRequired ||
        c.ProtectedFieldType == ProtectedFieldType.ChkTemplateQuestionCrosslinkIsAttachmentRequired ||
        c.ProtectedFieldType == ProtectedFieldType.ChkTemplateQuestionCrosslinkGroupCombobox
      ) {
        c.minResizableWidth = c.width = 200;
      } else if (c.ProtectedFieldType == ProtectedFieldType.ChkTemplateQuestionCrosslinkMaxScore) {
        c.width = 150;
      } else if (c.ProtectedFieldType == ProtectedFieldType.ChkQuestion) {
        c.width = 350;
      }
    });
  }

  private hideCustomColumns(): void {
    const chkQuestionMaxScore = this.gridOptions.columns.find(c => c.ProtectedFieldType == ProtectedFieldType.ChkQuestionMaxScore);
    if (chkQuestionMaxScore) {
      chkQuestionMaxScore.hidden = true;
    }

    const chkQuestionExtraInfo = this.gridOptions.columns.find(c => c.ProtectedFieldType == ProtectedFieldType.ChkQuestionExtraInfo);
    if (chkQuestionExtraInfo) {
      chkQuestionExtraInfo.hidden = true;
    }

    const chkQuestionTemplateSpecific = this.gridOptions.columns.find(c => c.ProtectedFieldType == ProtectedFieldType.ChkQuestionTemplateSpecific);
    if (chkQuestionTemplateSpecific) {
      chkQuestionTemplateSpecific.hidden = true;
    }

    const chkTemplateQuestionOrder = this.gridOptions.columns.find(c => c.ProtectedFieldType == ProtectedFieldType.ChkTemplateQuestionCrosslinkOrderID);
    if (chkTemplateQuestionOrder) {
      chkTemplateQuestionOrder.hidden = true;
    }
  }

  public readonly ProtectedFieldType = ProtectedFieldType;

  private hideOrShowNcNaBasedOnValues(hasNa: boolean, hasNc: boolean): void {
    const chkTemplateIsPossibleAnswerNA = this.gridOptions.columns.find(c => c.ProtectedFieldType == ProtectedFieldType.ChkTemplateQuestionCrosslinkIsPossibleAnswerNA);
    if (chkTemplateIsPossibleAnswerNA) {
      chkTemplateIsPossibleAnswerNA.hidden = !hasNa;
    }
    const chkTemplateIsPossibleAnswerNC = this.gridOptions.columns.find(c => c.ProtectedFieldType == ProtectedFieldType.ChkTemplateQuestionCrosslinkIsPossibleAnswerNC);
    if (chkTemplateIsPossibleAnswerNC) {
      chkTemplateIsPossibleAnswerNC.hidden = !hasNc;
    }
  }

  public openLinkGroupModal(): void {
    const lastgroups = this.gridOptions.dataSource.group;
    this.gridOptions.dataSource.group = [];
    this.setRowEditMode(false);
    this.dialog.open(QuestionBulkGroupComponent, {
      data: {
        records: this.selection,
        formId: this.formId,
        gridFieldId: this.formFieldId()!,
        collectionId: this.form()?.data?.CollectionsID
      }
    }).closed.pipe(take(1)).subscribe({
      next: (result) => {
        if (result) {
          this.selection = [];
          this.gridOptions.dataSource.group = lastgroups;
        }
      }
    });
  }

  public openGroupSortModal(): void {
    const lastgroups = this.gridOptions.dataSource.group;
    this.gridOptions.dataSource.group = [];
    this.setRowEditMode(false);
    this.dialog.open<CollectionFieldListOptionsConfiguration[]>(ChecklistsGroupSortModalComponent, {
      data: {
        questionGroups: this.checklistGroups,
        collectionId: this.form()?.data?.CollectionsID,
        collectionFieldValues: this.checklistGroupValues
      }
    }).closed.pipe(
      take(1)).subscribe((updatedGroups: CollectionFieldListOptionsConfiguration[] | undefined) => {
        //TODO:RV Retrieve updated question groups
        if (updatedGroups) {
          this.checklistGroups = updatedGroups;
          this.updateGroupFields(updatedGroups);
          //Needed so kendo shows the correct group names after updating the field values.
          this.gridOptions.dataSource.group = lastgroups;
        }
      }
    );
  }

  private updateGroupFields(updatedGroups: CollectionFieldListOptionsConfiguration[]): void {
    const records = structuredClone(this.sortedRecords());
    const groupFieldValues = this.mapGroupListOptionsToFieldValues(updatedGroups);
    records.forEach((record) => {
      const groupField = record.Fields[this.chkTemplateQuestionGroupIndex];
      if (groupField) {
        groupField.FieldValues = groupFieldValues;
      }
    });
    this.updateRecords(records);
  }

  private mapGroupListOptionsToFieldValues(groups: CollectionFieldListOptionsConfiguration[]): CollectionFormFieldValue[] {
    const languageId = this.store$.selectSignal(userSettingsFeature.selectUserLanguagesID)() ?? this.store$.selectSignal(userSettingsFeature.selectCompanyLanguagesID)();
    return groups.map(g => {
      return {
        CollectionFieldValuesID: g.TableFieldValueId,
        IndexPosition: g.IndexPosition,
        Caption: g.LanguageConfigurations.find(l => l.LanguageId == languageId)?.Caption ?? '',
        State: StateType.Active,
        FieldValueSortingType: FieldSortingType.Ascending
      };
    }) as CollectionFormFieldValue[];
  }

  public override onEditInlineClicked(record: Record, index: number): void {
    super.onEditInlineClicked(record, index);
    this.rowReorderable = false; //Can't reorder while editing rows
  }

  public onGroupChange(groups: GroupDescriptor[]): void {
    this.groups = groups;
    this.groups.forEach(g => g.compare = this.rowSortFn.bind(this));
    this.hideCustomColumns();
    const values = this.store$.selectSignal(selectFormFieldValues(this.formId, {}, [
      ProtectedFieldType.ChkTemplateIsPossibleAnswerNA,
      ProtectedFieldType.ChkTemplateIsPossibleAnswerNC]))();
    if (values) {
      const hasNa = values[ProtectedFieldType.ChkTemplateIsPossibleAnswerNA] as boolean;
      const hasNc = values[ProtectedFieldType.ChkTemplateIsPossibleAnswerNC] as boolean;
      this.hideOrShowNcNaBasedOnValues(hasNa, hasNc);
    }
  }

  public canDropRow(e: DropTargetEvent): boolean {
    if (e.dropTargetIndex == undefined || e.dropTargetIndex < 0) {
      return false;
    }

    const fromIndex = e.dragData;
    const toIndex = Number(e.dropTarget.getAttribute('data-kendo-grid-item-index') ?? -1);
    if (toIndex < 0) {
      return false;
    }
    if (fromIndex == toIndex) {
      return true;
    }
    const records = this.sortedRecords();
    const fromRecord = records[fromIndex];
    const toRecord = records[toIndex];
    const fromGroupField = fromRecord.Fields[this.chkTemplateQuestionGroupIndex];
    const toGroupField = toRecord.Fields[this.chkTemplateQuestionGroupIndex];
    return fromGroupField?.Value == toGroupField?.Value;
  }

  public onRowDragEnter(e: DropTargetEvent): void {
    if (!this.canDropRow(e)) {
      this.gridComponent.updateMouseCursor('not-allowed');
    } else {
      this.gridComponent.updateMouseCursor('move');
    }
  }

  public onRowDrop(e: DropTargetEvent): void {
    this.gridComponent.updateMouseCursor('move');
    if (!this.canDropRow(e) || e.dropTargetIndex == undefined)
      return;
    //toIndex is the index of the row where the dragged row is dropped
    const fromIndex = e.dragData;
    const toIndex = calculateDestinationIndex(e, 0, fromIndex, 0);
    const records = structuredClone(this.sortedRecords());
    if (toIndex < 0 || e.dragData < 0) {
      console.error(`Invalid  from or to index in row drop. fromIndex: ${fromIndex} toIndex: ${toIndex}`);
    }

    const movedItem = records[fromIndex];
    records.splice(fromIndex, 1);
    records.splice(toIndex, 0, movedItem);

    //Update the order of the records.
    //TODO: RV Handle restart order from 0 with each group and pagination.
    records.forEach((r, index) => r.Fields[this.chkTemplateQuestionOrderIndex].Value = index);
    records.sort(this.rowSortFn.bind(this));
    this.updateRecords(records);

  }

  public createQuestion(): void {
    this.createNewInstance();
  }

  public override createNewInstance(): void {
    const formFieldId = this.formFieldId();
    if (formFieldId) {
      if (this.externalAccess != null) {
        this.store$.dispatch(formsActions.getExternalGridLinkedForm({
          externalAccess: this.externalAccess,
          collectionFieldId: undefined,
          formId: this.formId,
          gridFieldId: formFieldId,
          recordId: undefined,
          relationType: LinkedCollectionType.GridRecord
        }));
      } else {
        this.store$.dispatch(formsActions.getGridLinkedFormWithProtectedFieldValue({
          formId: this.formId,
          externalAccess: this.externalAccess,
          collectionFieldId: undefined,
          recordId: undefined,
          gridFieldId: formFieldId,
          relationType: LinkedCollectionType.GridRecord,
          formFields: [{
            value: true,
            protectedFieldType: ProtectedFieldType.ChkQuestionTemplateSpecific
          }]
        }));
      }
    }
  }

  public searchTermSignal: WritableSignal<string | undefined> = signal(undefined);
  public override pagedRecords = computed(() => {
    const result = this.activeRecords().slice(this.pageRange().skip, this.pageRange().skip + this.pageRange().take);
    const term = this.searchTermSignal();
    if (term && term.trim() != '') {
      return result.filter(r => r.Fields.some(f => f.Value?.toString()?.toLowerCase()?.includes(term.toLowerCase())));
    }
    return result;
  });


  public onSearchTermChanged($event: string): void {
    this.searchTermSignal.set($event);
  }

  /**
   * Needed because we will always need to update the order of the records at once as we can't (kendo)sort and reorder at the same time.
   * @param {Record[]} records
   */
  private updateRecords(records: Record[]): void {
    if (this.formFieldId()) {
      this.store$.dispatch(formsActions.updateGridFormFieldRecords({
        formId: this.formId,
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        gridFieldId: this.formFieldId()!,
        records: records
      }));
    }
  }
}
